import React from "react";
import { motion } from "framer-motion";

const Terms = () => {
  return (
    <div className="content-page">
      {/* Hero Banner */}
      <div className="content-page-header">
        <div className="absolute inset-0 bg-hero-mesh" />
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-extrabold mb-3"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Terms of Service
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/60 text-sm"
          >
            Last updated: May 2025
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="content-page-body pb-16">
        <div className="content-card">
          <p className="text-muted mb-8 leading-relaxed">
            This Terms of Service agreement sets out the terms on which ClockIn Master provides its users with access to and the use of our product. By choosing to use ClockIn Master, customers acknowledge that they have read, understood and agreed to the terms and conditions of this agreement.
          </p>

          <Section title="1. Definitions">
            <BulletList items={[
              <><strong>"We," "our," or "us"</strong> refers to PIVOTIUM LTD, a company registered in the United Kingdom.</>,
              <><strong>"You" or "your"</strong> refers to the person or entity registered to use ClockIn Master Services.</>,
              <><strong>"Terms"</strong> means our Terms of Service, which includes our Privacy Policy.</>,
              <><strong>"Services"</strong> means our websites, apps (web, iOS and Android), APIs, content, and third-party services.</>,
              <><strong>"Information"</strong> means all forms of data you provide or that we collect from your use of the services.</>,
              <><strong>"Content"</strong> means any information, text, graphics, or other materials uploaded, downloaded, or appearing on the Services.</>,
            ]} />
          </Section>

          <Section title="2. General">
            <p className="text-gray-600 mb-3">Subject to your compliance with these Terms, ClockIn Master grants you a limited, non-transferable, non-exclusive, revocable license to access and use the Services.</p>
            <p className="text-gray-600 mb-3">To use ClockIn Master, you must:</p>
            <BulletList items={[
              "Be at least sixteen (16) years of age or the minimum age required in your jurisdiction",
              "Complete the registration process",
              "Provide current and accurate information",
              "Agree to these Terms",
              "Use the Services in compliance with all applicable laws and regulations",
              "Not use the Services for any illegal or unauthorized purpose",
              "Not interfere with or disrupt the Services",
            ]} />
          </Section>

          <Section title="3. Intellectual Property">
            <p className="text-gray-600 mb-3">You own your content. We do not claim any intellectual property rights over information you provide.</p>
            <p className="text-gray-600 mb-3">We own ClockIn Master and our Services. You may not copy, reproduce, alter, modify, resell, mirror, or create derivative works without written permission.</p>
            <p className="text-gray-600">You grant us a royalty-free, worldwide, transferable, sub-licensable, irrevocable and perpetual license to use any suggestions, enhancement requests, recommendations or other feedback.</p>
          </Section>

          <Section title="4. Passwords and Accounts">
            <BulletList items={[
              "You're responsible for keeping your account name and password confidential.",
              "You're responsible for any account that you have access to.",
              "You agree to notify us immediately of any unauthorized use of your account(s).",
              "We're not responsible for any losses due to stolen or hacked passwords.",
              "You will not represent that you are any other individual or entity unless given written permission.",
            ]} />
          </Section>

          <Section title="5. Payment Terms">
            <BulletList items={[
              "Free trial: fourteen (14) day free trial of the paid Services.",
              "All fees are exclusive of all taxes or duties imposed by governing authorities.",
              "A valid payment method is required to continue after the trial period ends.",
              "Services are billed in advance on a monthly basis and are non-refundable.",
              "No refunds for partial months, upgrades/downgrades, or months unused.",
              "We reserve the right to change fees upon thirty (30) days notice.",
            ]} />
          </Section>

          <Section title="6. Cancellation and Termination">
            <BulletList items={[
              "You are solely responsible for the proper cancellation of your account.",
              "Cancel at any time via Company Settings.",
              "An email or phone request is not considered cancellation.",
              "Upon cancellation, your account will be immediately disabled.",
              "We retain the right to retain or delete data at our discretion.",
              "We reserve the right to refuse service and close accounts without notice.",
            ]} />
          </Section>

          <Section title="7. Limitation of Liability">
            <p className="text-gray-600 mb-3">To the fullest extent permitted by law, you assume full responsibility for and we disclaim liability for any indirect, consequential, exemplary, incidental, or punitive damages.</p>
            <p className="text-gray-600 mb-3">We disclaim liability for acts, omissions, and conduct of any third parties related to your use of ClockIn Master.</p>
            <p className="text-gray-600">Our liability shall not exceed what you paid us for the Services in the previous month.</p>
          </Section>

          <Section title="8. Governing Law">
            <p className="text-gray-600 mb-3">These Terms shall be governed by the laws of England and Wales. Disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
            <p className="text-gray-600">Any claim must be brought within one (1) year after the cause of action arises.</p>
          </Section>

          <Section title="9. Changes to Terms">
            <p className="text-gray-600 mb-3">We reserve the right to modify these Terms at any time. Your continued use constitutes acceptance of the new Terms.</p>
            <BulletList items={[
              "Modify, suspend or terminate operation of or access to ClockIn Master",
              "Modify or change ClockIn Master and any applicable policies or terms",
              "Interrupt the operation as necessary for maintenance or changes",
            ]} />
          </Section>

          <Section title="10. Data Processing and Privacy">
            <BulletList items={[
              "We process personal data in accordance with UK Data Protection Act 2018 and UK GDPR.",
              "By using our Services, you agree to our Privacy Policy.",
              "We implement appropriate technical and organizational security measures.",
              "We may process data outside the UK with appropriate safeguards.",
              "You have the right to access, rectify, erase, restrict, or object to data processing.",
            ]} />
          </Section>

          <Section title="11. Service Availability and Support">
            <BulletList items={[
              "We strive to maintain 99.9% uptime, excluding scheduled maintenance.",
              "Scheduled maintenance announced at least 24 hours in advance.",
              "Support during business hours (Monday to Friday, 9:00 AM – 5:00 PM GMT).",
              "Emergency support available 24/7 for critical issues.",
              "We are not liable for damages from service interruptions or maintenance.",
            ]} />
          </Section>

          <Section title="12. Compliance and Legal Requirements">
            <BulletList items={[
              "You agree to comply with all applicable employment laws.",
              "You are responsible for maintaining accurate time and attendance records.",
              "We may disclose information to comply with legal obligations.",
              "You must maintain appropriate insurance coverage.",
              "You are responsible for obtaining necessary employee consents.",
            ]} />
          </Section>

          <Section title="13. Force Majeure">
            <p className="text-gray-600 mb-3">We shall not be liable for failure to perform if caused by circumstances beyond our control, including:</p>
            <BulletList items={[
              "Acts of God", "Natural disasters", "War or terrorism", "Government actions",
              "Internet service provider failures", "Power failures",
            ]} />
          </Section>

          <Section title="14. Severability">
            <p className="text-gray-600">If any provision is found unenforceable, it shall be limited so that these Terms otherwise remain in full force.</p>
          </Section>

          <Section title="15. Assignment">
            <p className="text-gray-600">You may not assign your rights. We may assign ours to any individual or entity at our discretion.</p>
          </Section>

          <Section title="16. No Waiver">
            <p className="text-gray-600">Failure to exercise any right shall not constitute a waiver of such right.</p>
          </Section>

          <Section title="17. Entire Agreement">
            <p className="text-gray-600">These Terms and all incorporated documents constitute the entire agreement between you and us.</p>
          </Section>

          <Section title="18. Dispute Resolution">
            <BulletList items={[
              "Disputes resolved through good faith negotiations.",
              "If unresolved within 30 days, either party may initiate mediation.",
              "Mediation conducted in London, UK, per CEDR rules.",
              "If mediation fails, binding arbitration under LCIA rules.",
              "Each party bears its own costs.",
            ]} />
          </Section>

          <Section title="19. System Requirements">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-lightgray rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Web Browser</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>Chrome (latest)</li><li>Firefox (latest)</li><li>Edge (latest)</li><li>Safari (latest)</li>
                </ul>
              </div>
              <div className="bg-lightgray rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Mobile App</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>iOS 14.0 or later</li><li>Android 8.0 or later</li>
                </ul>
              </div>
              <div className="bg-lightgray rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Internet</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>Min 2 Mbps download</li><li>Stable connection for real-time</li>
                </ul>
              </div>
            </div>
          </Section>

          <Section title="20. Feature-Specific Terms">
            <div className="space-y-4">
              {[
                { name: "Time Tracking", items: ["Location tracking is optional and requires consent", "Time entries recorded in UTC, converted to local time", "Manual adjustments require manager approval"] },
                { name: "Scheduling", items: ["Shift changes require 24 hours notice", "Overtime must be pre-approved", "Schedule conflicts resolved based on seniority"] },
                { name: "Payroll Integration", items: ["Payroll data processed per local tax laws", "Export formats comply with major payroll systems", "Data retention follows financial record-keeping requirements"] },
              ].map((feat) => (
                <div key={feat.name} className="bg-lightgray rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">{feat.name}</h4>
                  <ul className="space-y-1">
                    {feat.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                        <span className="w-1 h-1 rounded-full bg-secondary mt-1.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          <Section title="21. Third-Party Services">
            <BulletList items={[
              "We may integrate with third-party services to enhance functionality.",
              "Use of third-party services is subject to their terms.",
              "We are not responsible for third-party performance.",
              "Data shared with third parties is subject to their privacy policies.",
              "You may opt out of specific integrations.",
            ]} />
          </Section>

          <Section title="22. Export Controls">
            <BulletList items={[
              "The Services may be subject to export control laws.",
              "You agree to comply with all applicable export control laws.",
              "You represent that you are not in a sanctioned country.",
              "You will not use the Services for prohibited purposes.",
            ]} />
          </Section>

          <Section title="23. System Issues and Wage Calculation">
            <p className="text-gray-600 mb-3">ClockIn Master is not liable for disputes from time tracking data or labor cost calculations.</p>
            <BulletList items={[
              "The company is responsible for reviewing data accuracy.",
              "Support tickets submitted via email to hello@clockinmaster.com.",
              "ClockIn Master is not responsible for wage overpayments or underpayments.",
              "The company must verify all data before processing payroll.",
            ]} />
          </Section>

          <Section title="24. Benefits of Using ClockInMaster">
            <BulletList items={[
              <><strong>Increased Productivity:</strong> Automate time-consuming tasks.</>,
              <><strong>Better Team Management:</strong> Keep your team aligned.</>,
              <><strong>Improved Communication:</strong> Streamline team communication.</>,
              <><strong>Enhanced Security:</strong> Enterprise-grade data protection.</>,
              <><strong>Data-Driven Decisions:</strong> Comprehensive analytics.</>,
              <><strong>Professional Support:</strong> Expert help whenever you need it.</>,
            ]} />
          </Section>

          <Section title="25. Contact Us">
            <p className="text-gray-600 mb-3">If you have any questions about these Terms:</p>
            <div className="bg-lightgray rounded-xl p-5 text-sm text-gray-600 space-y-1">
              <p><strong>Email:</strong> hello@clockinmaster.com</p>
              <p><strong>Phone:</strong> +44 7407822792</p>
              <p><strong>Company:</strong> PIVOTIUM LTD</p>
              <p><strong>Address:</strong> 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ</p>
              <p><strong>D-U-N-S®:</strong> 231644482</p>
              <a href="https://find-and-update.company-information.service.gov.uk/company/15612152" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline inline-block mt-1">
                Company Registration Details →
              </a>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-8 last:mb-0">
    <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2 mb-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-gray-600 text-sm">
        <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default Terms;